// Feature Documentation: docs/features/nma-coding-review-approval.md
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';
import { FeatureFlagsProvider } from './FeatureFlags';
import { callApi } from '../callApi';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Button,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Flex,
  IconButton,
  Tooltip,
  VStack,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, DownloadIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';

function ReviewCase() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decisions, setDecisions] = useState({}); // { [claimType_cptCode]: 'accept' | 'deny' | 'recode' }
  const [pdfUrls, setPdfUrls] = useState({});
  const [nextCaseId, setNextCaseId] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const { isOpen: isTextModalOpen, onOpen: onTextModalOpen, onClose: onTextModalClose } = useDisclosure();
  const [selectedTextFile, setSelectedTextFile] = useState(null);
  const { accessToken } = useContext(AppContext);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchCaseData = async () => {
      try {
        const response = await callApi(`/api/review/cases/${caseId}`, accessToken, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const data = await response.json();
        setCaseData(data);
        
        // Initialize decisions state
        const initialDecisions = {};
        data.codes_to_review.forEach(claimGroup => {
          claimGroup.cpt_codes.forEach(code => {
            const key = `${claimGroup.claim_type}_${code.cpt_code}`;
            // Default to accept if not preliminary, otherwise leave undefined
            if (code.type !== 'preliminary') {
              initialDecisions[key] = 'accept';
            }
          });
        });
        setDecisions(initialDecisions);





      } catch (error) {
        console.error('Error fetching review case:', error);
        toast({
          title: 'Error loading case',
          description: 'Could not load the case details.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    if (accessToken && caseId) {
      fetchCaseData();
    }
  }, [accessToken, caseId, toast]);

  // Lazy load PDFs when their tab becomes active
  useEffect(() => {
    if (caseData && caseData.case_files && caseData.case_files.length > 0) {
      const activeFile = caseData.case_files[activeTabIndex];
      if (activeFile && !pdfUrls[activeFile.id]) {
        fetchPdf(activeFile.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabIndex, caseData]);

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          case_id: caseId,
          file_id: file.id 
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);
      } else {
        toast({
          title: 'Download Failed',
          description: 'Could not download the file.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const fetchPdf = async (fileId) => {
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          file_id: fileId,
          disposition: 'inline'
        })
      });
      
      if (response.ok) {
        const rawBlob = await response.blob();
        // Explicitly cast the blob to application/pdf to prevent browser download fallback
        const pdfBlob = new Blob([rawBlob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        setPdfUrls(prev => ({ ...prev, [fileId]: url }));
      }
    } catch (error) {
      console.error(`Error fetching PDF ${fileId}:`, error);
    }
  };

  const handleDecision = (claimType, index, decision) => {
    setDecisions(prev => ({
      ...prev,
      [`${claimType}_${index}`]: decision
    }));
  };

  // Calculate unresolved items
  let unresolvedCount = 0;
  if (caseData) {
    caseData.codes_to_review.forEach(claimGroup => {
      const sortedCodes = [...claimGroup.cpt_codes].sort((a, b) => (a.cpt_code || '').localeCompare(b.cpt_code || ''));
      sortedCodes.forEach((code, index) => {
        if (code.type === 'preliminary') {
          const key = `${claimGroup.claim_type}_${index}`;
          if (!decisions[key]) {
            unresolvedCount++;
          }
        }
      });
    });
  }

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Submit each claim type group
      for (const claimGroup of caseData.codes_to_review) {
        const sortedCodes = [...claimGroup.cpt_codes].sort((a, b) => (a.cpt_code || '').localeCompare(b.cpt_code || ''));
        
        const formattedDecisions = sortedCodes.map((code, index) => {
          const key = `${claimGroup.claim_type}_${index}`;
          return {
            cpt_data: code,
            // If it's preliminary and has no decision, mark as unresolved so backend keeps the flag
            decision: decisions[key] || (code.type === 'preliminary' ? 'unresolved' : 'accept')
          };
        });

        await callApi(`/api/cases/${caseId}/coding-approval`, accessToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            claim_type: claimGroup.claim_type,
            source_adjusted_record_id: claimGroup.source_adjusted_record_id,
            approval_action: 'reviewed',
            decisions: formattedDecisions
          })
        });
      }

      toast({
        title: 'Review Complete',
        description: 'The coding review has been successfully saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Fetch the queue fresh AFTER saving to ensure we get a truly unreviewed case
      try {
        const queueResponse = await callApi('/api/review/queue', accessToken, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const queueData = await queueResponse.json();
        
        if (queueData.queue && queueData.queue.length > 0) {
          // Find the first case in the queue that is NOT the current case
          const nextCase = queueData.queue.find(item => item.internal_case_id !== caseId);
          
          if (nextCase) {
            // The key wrapper in App.js will automatically wipe all state when the URL changes!
            navigate(`/review/${nextCase.internal_case_id}`);
            return; // Exit early so we don't hit the else block
          }
        }
      } catch (qError) {
        console.error('Error fetching fresh queue for next case:', qError);
      }

      // If we get here, either the queue is empty, or the fetch failed
      toast({
        title: 'Queue Empty',
        description: 'There are no more cases needing review.',
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      setSubmitting(false);
      navigate('/review-dashboard');
      
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Submission Error',
        description: 'There was an error saving your review. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8} centerContent>
        <Spinner size="xl" />
        <Text mt={4}>Loading case data...</Text>
      </Container>
    );
  }

  if (!caseData) return null;

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        
        {/* Top Section: Review Interface */}
        <Card mb={8}>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Box>
                <Flex align="center" gap={4} mb={1}>
                  <Button 
                    variant="link" 
                    colorScheme="blue" 
                    size="sm" 
                    onClick={() => navigate('/review-dashboard')}
                  >
                    &larr; Back to Dashboard
                  </Button>
                </Flex>
                <Heading size="md">Coding Review</Heading>
                <Text fontSize="sm" color="gray.500">
                  Case: {caseData.external_case_id} | Patient: {caseData.patient_name}
                </Text>
              </Box>
              <Box textAlign="right">
                <Button 
                  colorScheme="blue" 
                  onClick={handleSubmit} 
                  isLoading={submitting}
                >
                  Review Complete
                </Button>
                {unresolvedCount > 0 && (
                  <Text fontSize="xs" color="orange.500" mt={1} fontWeight="medium">
                    {unresolvedCount} unresolved item{unresolvedCount !== 1 ? 's' : ''}
                  </Text>
                )}
              </Box>
            </Flex>
          </CardHeader>
          <CardBody>
            <Grid templateColumns="1fr 1fr" gap={6}>
              {caseData.codes_to_review.map((claimGroup, idx) => (
                <GridItem key={idx}>
                  <Heading size="sm" mb={4} textTransform="uppercase" color="gray.600">
                    {claimGroup.claim_type} Claim
                  </Heading>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Code Details</Th>
                        <Th>Justification</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {([...claimGroup.cpt_codes].sort((a, b) => (a.cpt_code || '').localeCompare(b.cpt_code || ''))).map((code, cIdx) => {
                        const isPreliminary = code.type === 'preliminary';
                        const key = `${claimGroup.claim_type}_${cIdx}`;
                        const decision = decisions[key];
                        
                        let rowBg = 'transparent';
                        if (isPreliminary) {
                          if (decision === 'accept') rowBg = 'green.50';
                          else if (decision === 'deny') rowBg = 'red.50';
                          else rowBg = 'orange.50';
                        }

                        return (
                          <Tr key={cIdx} bg={rowBg}>
                            <Td verticalAlign="top">
                              <Box textDecoration={decision === 'deny' ? 'line-through' : 'none'}>
                                <Text fontWeight="bold">{code.cpt_code}</Text>
                                <Text fontSize="sm" color="gray.600">{code.units}</Text>
                                <Text fontSize="sm" color="gray.600">{code.modifiers && code.modifiers.length > 0 ? code.modifiers.join(', ') : code.modifier || 'None'}</Text>
                              </Box>
                            </Td>
                            <Td verticalAlign="top">
                              <Box>
                                {isPreliminary && <Badge colorScheme="orange" mr={2} mb={1}>Review</Badge>}
                                <Text fontSize="sm" whiteSpace="pre-wrap">
                                  {code.reason || code.justification}
                                </Text>
                              </Box>
                            </Td>
                            <Td verticalAlign="top">
                              {isPreliminary ? (
                                <Flex gap={2}>
                                  <Tooltip label="Accept">
                                    <IconButton 
                                      icon={<CheckIcon />} 
                                      size="sm" 
                                      colorScheme={decision === 'accept' ? 'green' : 'gray'}
                                      variant={decision === 'accept' ? 'solid' : 'outline'}
                                      onClick={() => handleDecision(claimGroup.claim_type, cIdx, 'accept')}
                                    />
                                  </Tooltip>
                                  <Tooltip label="Deny">
                                    <IconButton 
                                      icon={<CloseIcon />} 
                                      size="sm" 
                                      colorScheme={decision === 'deny' ? 'red' : 'gray'}
                                      variant={decision === 'deny' ? 'solid' : 'outline'}
                                      onClick={() => handleDecision(claimGroup.claim_type, cIdx, 'deny')}
                                    />
                                  </Tooltip>
                                </Flex>
                              ) : (
                                <Badge colorScheme="green">Auto-Accepted</Badge>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </GridItem>
              ))}
            </Grid>
          </CardBody>
        </Card>

        {/* Bottom Section: Document Viewer */}
        <Card h="800px">
          <CardHeader pb={0}>
            <Heading size="md">Case Documents</Heading>
          </CardHeader>
          <CardBody h="100%">
            {caseData.case_files && caseData.case_files.length > 0 ? (
              <Tabs h="100%" display="flex" orientation="vertical" variant="enclosed" index={activeTabIndex} onChange={(index) => setActiveTabIndex(index)}>
                <TabList w="300px" overflowY="auto" borderRight="1px solid" borderColor="gray.200">
                  {caseData.case_files.map(file => (
                    <Tab 
                      key={file.id} 
                      fontSize="sm" 
                      textAlign="left" 
                      justifyContent="flex-start"
                      whiteSpace="normal"
                      wordBreak="break-all"
                      p={4}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      _selected={{ bg: "blue.50", borderColor: "blue.200", borderRight: "none" }}
                    >
                      <Flex justify="space-between" align="start" w="100%">
                        <VStack align="start" spacing={1} flex="1" mr={2}>
                          <Text fontWeight="medium" noOfLines={2} title={file.file_name}>
                            {file.file_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatFileSize(file.file_size)}
                          </Text>
                        </VStack>
                        <HStack spacing={1}>
                          <IconButton
                            icon={<ViewIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="View file text"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTextFile(file);
                              onTextModalOpen();
                            }}
                          />
                          <IconButton
                            icon={<DownloadIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="Download file"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDownload(file);
                            }}
                          />
                        </HStack>
                      </Flex>
                    </Tab>
                  ))}
                </TabList>
                <TabPanels flex="1" overflow="hidden" pl={4}>
                  {caseData.case_files.map(file => (
                    <TabPanel key={file.id} h="100%" p={0}>
                      {pdfUrls[file.id] ? (
                        <iframe 
                          src={pdfUrls[file.id]} 
                          title={`Document Viewer - ${file.file_name}`}
                          width="100%" 
                          height="100%" 
                          style={{ border: 'none', borderRadius: '4px' }} 
                        />
                      ) : (
                        <Flex justify="center" align="center" h="100%">
                          <Spinner />
                        </Flex>
                      )}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            ) : (
              <Text>No documents available for this case.</Text>
            )}
          </CardBody>
        </Card>

      </Container>

      {/* Text Viewer Modal */}
      <Modal isOpen={isTextModalOpen} onClose={onTextModalClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedTextFile?.file_name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg="gray.50" 
              maxH="500px" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedTextFile?.file_text || "No text content available for this file."}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedTextFile ? formatFileSize(selectedTextFile.file_size) : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onTextModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
}

export default ReviewCase;
